// PDK API Helper Functions
//
// On shadow.* deployments (shadow.app.intrlock.io, shadow.admin.intrlock.io):
//   All calls route through shadow connector-api at shadow.api.intrlock.io.
//   SPA never calls PDK directly — connector-api holds the encrypted creds.
//
// On CES deployments (app.intrlock.io, admin.intrlock.io):
//   Calls route through the legacy intrlock-pdk-proxy Lambda at api.intrlock.io/pdk.
//   The proxy handles 2-step OAuth (client_credentials → id_token → system_token) server-side.
//
// This file is the ONLY PDK surface in the SPA. Zero direct PDK API calls anywhere else.
import { apiOrigin } from '../utils/surface';

// The new Intrlock surfaces always use connector-api. The legacy CES proxy is
// retained below only as a source-compatible fallback until the DNS cutover is
// complete; it is not selected by the new bundle.
const USE_CONNECTOR_API = true;

const PDK_PROXY = `${apiOrigin()}/pdk`;

// Shadow connector-api endpoints
const CONNECTOR_ACTION = `${apiOrigin()}/connector/action`;
const ADMIN_CONNECTOR_ACTION = `${apiOrigin()}/admin-connector/action`;

function getShadowToken(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem('intrlock_token') || window.localStorage.getItem('intrlock_jwt') || '';
}

// ── Shadow connector-api helper ──────────────────────────────────────────────
// Routes PDK actions through connector-api on shadow. property_id is passed
// as params so the connector can look up the entity_integrations row.
// For list_cloud_nodes / get_device_states / list_devices: pass property_id
// For try_open / virtual_read: pass cn_id + device_id + holder_id as needed.

async function connectorAction(
  action: string,
  params: Record<string, unknown>,
  useAdminConnector = false,
): Promise<any> {
  const token = getShadowToken();
  const url = useAdminConnector ? ADMIN_CONNECTOR_ACTION : CONNECTOR_ACTION;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ integration_key: 'pdk', action, params }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error || (err as any).message || res.statusText);
  }
  const wrapped = await res.json();
  // connector-api returns { integration_key, action, data, ... } — unwrap data for SPA parity
  return (wrapped && typeof wrapped === 'object' && 'data' in wrapped) ? (wrapped as any).data : wrapped;
}

// ── Legacy CES PDK proxy helper ──────────────────────────────────────────────

async function pdkFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(`${PDK_PROXY}/${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  return response;
}

async function pdkGet(path: string): Promise<any> {
  const res = await pdkFetch(path);
  if (!res.ok) throw new Error(`PDK API error: ${res.status} ${res.statusText}`);
  return res.json();
}

async function pdkPost(path: string, body?: any): Promise<Response> {
  return pdkFetch(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

// ── System info ──────────────────────────────────────────────────────────────

export async function getPDKAccessToken(systemId?: string): Promise<string> {
  return 'proxy-managed';
}

export async function getPDKSystemToken(systemId: string): Promise<string> {
  return 'proxy-managed';
}

export async function validatePDKSystem(systemId: string): Promise<boolean> {
  try {
    if (USE_CONNECTOR_API) {
      await connectorAction('list_cloud_nodes', { panel_id: systemId });
      return true;
    }
    const res = await pdkFetch(`systems/${systemId}`);
    return res.ok;
  } catch { return false; }
}

export async function getPDKSystemInfo(systemId: string): Promise<any> {
  if (USE_CONNECTOR_API) {
    return connectorAction('list_cloud_nodes', { panel_id: systemId });
  }
  return pdkGet(`systems/${systemId}`);
}

// ── Cloud nodes ──────────────────────────────────────────────────────────────

export async function getPDKCloudNodes(systemId: string): Promise<any[]> {
  if (USE_CONNECTOR_API) {
    return connectorAction('list_cloud_nodes', { panel_id: systemId });
  }
  return pdkGet(`systems/${systemId}/cloud-nodes`);
}

export async function getPDKSystemPanels(systemId: string): Promise<any[]> {
  return getPDKCloudNodes(systemId);
}

// ── Devices ──────────────────────────────────────────────────────────────────

export async function getPDKSystemDevices(systemId: string): Promise<any[]> {
  if (USE_CONNECTOR_API) {
    return connectorAction('list_devices', { panel_id: systemId });
  }
  const allDevices: any[] = [];
  let page = 0;
  while (true) {
    const batch = await pdkGet(`systems/${systemId}/devices?page=${page}&per_page=100`);
    if (!Array.isArray(batch) || batch.length === 0) break;
    allDevices.push(...batch);
    if (batch.length < 100) break;
    page++;
  }
  return allDevices;
}

export async function getPDKSystemDoors(systemId: string): Promise<any[]> {
  return getPDKSystemDevices(systemId);
}

export async function getPDKDeviceStatus(systemId: string, deviceId: string): Promise<any> {
  if (USE_CONNECTOR_API) {
    return connectorAction('get_device_states', { panel_id: systemId });
  }
  return pdkGet(`systems/${systemId}/devices/${deviceId}`);
}

// ── Holders ──────────────────────────────────────────────────────────────────

export async function getPDKSystemHolders(systemId: string): Promise<any[]> {
  if (USE_CONNECTOR_API) {
    return connectorAction('list_holders', { panel_id: systemId });
  }
  const allHolders: any[] = [];
  let page = 0;
  while (true) {
    const batch = await pdkGet(`systems/${systemId}/holders?page=${page}&per_page=100`);
    if (!Array.isArray(batch) || batch.length === 0) break;
    allHolders.push(...batch);
    if (batch.length < 100) break;
    page++;
  }
  return allHolders;
}

export async function getPDKSystemPersons(systemId: string): Promise<any[]> {
  return getPDKSystemHolders(systemId);
}

export async function getPDKHolder(systemId: string, holderId: string): Promise<any> {
  if (USE_CONNECTOR_API) {
    return connectorAction('get_holder', { panel_id: systemId, holder_id: holderId });
  }
  return pdkGet(`systems/${systemId}/holders/${holderId}`);
}

// ── Door control ─────────────────────────────────────────────────────────────

export async function controlPDKDoor(
  systemId: string,
  deviceId: string,
  action: 'open' | 'close',
  cloudNodeId?: string,
  holderId?: string
): Promise<void> {
  if (USE_CONNECTOR_API) {
    // Shadow: all unlocks through connector-api admin-connector (super_admin only)
    // try_open does not require holder attribution (admin unlock use case)
    await connectorAction(
      'try_open',
      {
        panel_id: systemId,
        cn_id: cloudNodeId || '',
        device_id: deviceId,
        ...(holderId ? { holder_id: holderId } : {}),
      },
      /* useAdminConnector= */ true,
    );
    return;
  }

  // CES path — legacy proxy
  if (cloudNodeId && holderId) {
    const res = await pdkPost(
      `systems/${systemId}/cloud-nodes/${cloudNodeId}/devices/${deviceId}/virtual-read`,
      { holderId }
    );
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`Virtual-read failed: ${res.status} ${errText}`);
    }
  } else if (cloudNodeId) {
    const res = await pdkPost(
      `systems/${systemId}/cloud-nodes/${cloudNodeId}/devices/${deviceId}/open`
    );
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`Open failed: ${res.status} ${errText}`);
    }
  } else {
    const res = await pdkPost(`systems/${systemId}/devices/${deviceId}/${action}`);
    if (!res.ok) throw new Error(`Failed to ${action} device: ${res.statusText}`);
  }
}

// ── Live device states ───────────────────────────────────────────────────────

export async function getPDKLiveDevices(systemId: string, cloudNodeIds?: string[]): Promise<any[]> {
  if (USE_CONNECTOR_API) {
    // Shadow: get cloud nodes then device states through connector-api
    const nodes = await connectorAction('list_cloud_nodes', { panel_id: systemId });
    const nodeList = Array.isArray(nodes) ? nodes : [];
    const filteredNodes = cloudNodeIds && cloudNodeIds.length > 0
      ? nodeList.filter((n: any) => cloudNodeIds.includes(String(n.id)))
      : nodeList;

    const allDevices: any[] = [];
    for (const node of filteredNodes) {
      try {
        const [devs, states] = await Promise.all([
          connectorAction('list_devices', { panel_id: systemId, cn_id: node.id }),
          connectorAction('get_device_states', { panel_id: systemId, cn_id: node.id }),
        ]);
        const devsArr = Array.isArray(devs) ? devs : [];
        const statesArr = Array.isArray(states) ? states : [];
        const stateMap: Record<string, any> = {};
        for (const s of statesArr) stateMap[s.id] = s;

        const nodeOnline = node.connectionStatus?.connected === true;
        for (const d of devsArr) {
          const deviceId = d.external_id || d.id;
          const metadata = d.metadata && typeof d.metadata === 'object' ? d.metadata : {};
          const state = stateMap[deviceId];
          const relayOpen = state?.relayOpen ?? state?.forceOpen ?? metadata.relay_unlocked ?? false;
          const dpsOpen = state?.dpsOpen ?? metadata.door_open ?? false;
          allDevices.push({
            id: deviceId,
            name: d.display_name || d.name || d.displayName || d.description || 'Unknown Device',
            type: d.external_type === 'reader' ? 'lock' : d.external_type || ((d.type || d.deviceType) === 'primaryReader' ? 'lock' : (d.type || d.deviceType) === 'relay' ? 'relay' : d.type || d.deviceType || 'lock'),
            connection_status: d.status || (nodeOnline && !state?.communicationLost ? 'online' : 'offline'),
            lock_status: relayOpen ? 'unlocked' : 'locked',
            has_dps: metadata.has_dps ?? d.dps ?? false,
            dps_open: dpsOpen,
            relay_open: relayOpen,
            force_open: state?.forceOpen || false,
            force_close: state?.forceClose || false,
            auto_open: state?.autoOpen || false,
            communication_lost: state?.communicationLost || false,
            controller_issues: state?.controllerIssues || false,
            prop_alarm_on: state?.propAlarmOn || false,
            forced_alarm_on: state?.forcedAlarmOn || false,
            manufacturer: 'PDK',
            model: metadata.model || d.model || d.modelName || d.productName || d.hardwareVersion || d.type || '',
            firmware_version: state?.firmwareVersion || state?.firmware_version || metadata.firmware_version || d.firmwareVersion || d.firmware_version || '',
            serial_number: metadata.serial_number || d.serialNumber || d.serial_number || d.serial || d.macAddress || '',
            battery_percent: state?.batteryPercent ?? state?.battery_percent ?? state?.batteryLevel ?? state?.battery_level ?? metadata.battery_percent ?? d.batteryPercent ?? d.battery_level ?? null,
            battery_state: state?.batteryState || state?.battery_state || metadata.battery_state || d.batteryState || d.battery_state || '',
            acs_device_id: deviceId,
            acs_provider: 'pdk',
            cloud_node_id: node.id,
            cloud_node_name: node.name || '',
            cloud_node_serial_number: node.serialNumber || node.serial_number || '',
            property_id: 0,
          });
        }
      } catch { /* ignore per-node failures */ }
    }
    return allDevices;
  }

  // CES legacy path
  const allNodes = await pdkGet(`systems/${systemId}/cloud-nodes`);
  const nodes = cloudNodeIds && cloudNodeIds.length > 0
    ? allNodes.filter((n: any) => cloudNodeIds.includes(String(n.id)))
    : allNodes;

  const deviceMap: Record<string, any> = {};
  const stateMap: Record<string, any> = {};
  const deviceNodeMap: Record<string, string> = {};

  for (const node of nodes) {
    try {
      const devs = await pdkGet(`systems/${systemId}/cloud-nodes/${node.id}/devices?per_page=100`);
      if (Array.isArray(devs)) {
        for (const d of devs) {
          deviceMap[d.id] = d;
          deviceNodeMap[d.id] = node.id;
        }
      }
      const states = await pdkGet(`systems/${systemId}/cloud-nodes/${node.id}/devices/states?per_page=100`);
      if (Array.isArray(states)) {
        for (const s of states) {
          stateMap[s.id] = s;
        }
      }
    } catch { /* ignore per-node failures */ }
  }

  const devices = Object.values(deviceMap);
  const nodeOnlineMap: Record<string, boolean> = {};
  for (const n of nodes) {
    nodeOnlineMap[n.id] = n.connectionStatus?.connected === true;
  }
  const anyNodeOnline = nodes.some((n: any) => n.connectionStatus?.connected === true);

  return devices.map((d: any) => {
    const state = stateMap[d.id];
    const isOnline = anyNodeOnline && !(state?.communicationLost);
    const relayOpen = state?.relayOpen || state?.forceOpen || false;

    return {
      id: d.id,
      name: d.name || 'Unknown Device',
      type: d.type === 'primaryReader' ? 'lock' : d.type === 'relay' ? 'relay' : d.type || 'lock',
      connection_status: isOnline ? 'online' : 'offline',
      lock_status: relayOpen ? 'unlocked' : 'locked',
      has_dps: d.dps || false,
      dps_open: state?.dpsOpen || false,
      relay_open: relayOpen,
      force_open: state?.forceOpen || false,
      force_close: state?.forceClose || false,
      auto_open: state?.autoOpen || false,
      communication_lost: state?.communicationLost || false,
      controller_issues: state?.controllerIssues || false,
      prop_alarm_on: state?.propAlarmOn || false,
      forced_alarm_on: state?.forcedAlarmOn || false,
      manufacturer: 'PDK',
      model: d.hardwareVersion || d.type || '',
      firmware_version: d.firmwareVersion || '',
      serial_number: d.serialNumber || '',
      acs_device_id: d.id,
      acs_provider: 'pdk',
      cloud_node_id: deviceNodeMap[d.id] || '',
      cloud_node_name: nodes.find((n: any) => n.id === deviceNodeMap[d.id])?.name || '',
      property_id: 0,
    };
  });
}

export async function getPDKPanelDevices(systemId: string, cloudNodeId: string): Promise<any[]> {
  if (USE_CONNECTOR_API) {
    return connectorAction('list_devices', { panel_id: systemId, cn_id: cloudNodeId });
  }
  try {
    return await pdkGet(`systems/${systemId}/cloud-nodes/${cloudNodeId}/devices`);
  } catch {
    return getPDKSystemDevices(systemId);
  }
}
